<template>
	<div class="relative bg-gray-50 overflow-hidden">
		<div
			class="absolute top-0 right-0 w-80 h-80 bg-brand-cyan opacity-5 rounded-full blur-3xl pointer-events-none"
		/>
		<div
			class="absolute bottom-0 left-0 w-64 h-64 bg-primary opacity-5 rounded-full blur-3xl pointer-events-none"
		/>

		<section ref="mainSection" class="relative py-16 lg:py-20">
			<div class="container px-4 mx-auto max-w-7xl">
				<div class="text-center max-w-3xl mx-auto mb-12">
					<div
						ref="badge"
						class="inline-flex items-center gap-2 px-3 py-1 mb-4 text-xs font-bold tracking-wider text-primary bg-brand-cyan opacity-10 rounded-full transition-all duration-700 ease-out transform scale-95 opacity-0"
					>
						<span
							class="w-1.5 h-1.5 bg-accent rounded-full animate-pulse"
						/>
						ABOUT NEXUS INSURANCE
					</div>

					<h1
						ref="heading"
						class="text-3xl lg:text-4xl font-extrabold text-primary leading-tight mb-4 transition-all duration-700 ease-out transform scale-95 opacity-0"
					>
						Insurance <span class="text-accent">Simplified</span>
					</h1>

					<p
						ref="subtitle"
						class="text-base text-text-secondary leading-relaxed transition-all duration-700 ease-out transform scale-95 opacity-0"
					>
						Breaking down barriers and misconceptions to make
						insurance accessible, transparent, and genuinely
						protective.
					</p>
				</div>

				<div
					class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
				>
					<div
						ref="misconceptionCard"
						class="bg-white rounded-xl shadow-md border border-gray-100 p-6 transition-all duration-700 ease-out transform scale-95 opacity-0 hover:shadow-xl hover:border-accent"
					>
						<div
							class="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4"
						>
							<svg
								class="w-6 h-6 text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
								/>
							</svg>
						</div>
						<h3 class="text-lg font-bold text-primary mb-2">
							The Common Misconception
						</h3>
						<p class="text-sm text-text-secondary leading-relaxed">
							The most widely held misconception is that Insurance
							Companies do not want to pay claims. This is, quite
							simply,
							<span class="font-bold text-accent">NOT TRUE</span>.
						</p>
					</div>

					<div
						ref="realityCard"
						class="bg-white rounded-xl shadow-md border border-gray-100 p-6 transition-all duration-700 ease-out transform scale-95 opacity-0 hover:shadow-xl hover:border-brand-cyan"
					>
						<div
							class="w-12 h-12 bg-brand-cyan rounded-lg flex items-center justify-center mb-4"
						>
							<svg
								class="w-6 h-6 text-primary"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
						<h3 class="text-lg font-bold text-primary mb-2">
							The Reality
						</h3>
						<p class="text-sm text-text-secondary leading-relaxed">
							The
							<span class="font-bold text-brand-cyan">TRUTH</span>
							is Insurance Companies want to pay genuine claims
							that comply with the terms and conditions of your
							insurance contract.
						</p>
					</div>

					<div
						ref="keyCard"
						class="bg-white rounded-xl shadow-md border border-gray-100 p-6 transition-all duration-700 ease-out transform scale-95 opacity-0 hover:shadow-xl hover:border-primary"
					>
						<div
							class="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4"
						>
							<svg
								class="w-6 h-6 text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
								/>
							</svg>
						</div>
						<h3 class="text-lg font-bold text-primary mb-2">
							The Key to Success
						</h3>
						<p class="text-sm text-text-secondary leading-relaxed">
							Most issues that arise when making a claim result
							from not understanding how insurance works and our
							role and responsibility in the insurance contract.
						</p>
					</div>
				</div>

				<div class="max-w-4xl mx-auto">
					<div
						ref="recommendationCard"
						class="bg-primary rounded-xl shadow-lg p-8 transition-all duration-700 ease-out transform scale-95 opacity-0 hover:shadow-xl"
					>
						<div
							class="flex flex-col md:flex-row items-start md:items-center gap-6"
						>
							<div
								class="flex-shrink-0 w-16 h-16 rounded-lg bg-white flex items-center justify-center"
							>
								<svg
									class="w-8 h-8 text-brand-cyan"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</div>
							<div class="flex-1">
								<h3
									class="text-sm font-bold text-brand-cyan uppercase tracking-wide mb-2"
								>
									Our Recommendation
								</h3>
								<p
									class="text-base text-white leading-relaxed mb-4"
								>
									Nexus recommends reading your policy with
									guidance from an IRA-licensed intermediary,
									agent, broker, or lawyer to fully understand
									your coverage.
								</p>
								<a
									href="https://www.ira.go.ke/"
									target="_blank"
									rel="noopener noreferrer"
									class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-primary bg-white rounded-lg hover:bg-brand-cyan hover:text-white transition-all duration-300"
								>
									Visit IRA Kenya
									<svg
										class="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
										/>
									</svg>
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
export default {
	name: 'AboutUs',

	data() {
		return {
			observer: null,
		};
	},

	mounted() {
		this.initScrollAnimations();
	},

	beforeUnmount() {
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
	},

	methods: {
		/**
		 * Initialize Intersection Observer for scroll-triggered animations
		 */
		initScrollAnimations() {
			// Changed threshold to 0.05 (5%) for a slightly earlier trigger
			const observerOptions = {
				threshold: 0.05,
				rootMargin: '0px 0px -50px 0px',
			};

			this.observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						this.animateContent();
						this.observer.disconnect();
					}
				});
			}, observerOptions);

			if (this.$refs.mainSection) {
				this.observer.observe(this.$refs.mainSection);
			}
		},

		/**
		 * Sequentially animate all content elements with staggered delays
		 */
		animateContent() {
			// Adjusted delays for a smoother, less immediate cascade
			const animationSequence = [
				// Header Content
				{ ref: 'badge', delay: 100 },
				{ ref: 'heading', delay: 200 },
				{ ref: 'subtitle', delay: 300 },
				// Cards Grid (more gentle staggering)
				{ ref: 'misconceptionCard', delay: 450 },
				{ ref: 'realityCard', delay: 550 },
				{ ref: 'keyCard', delay: 650 },
				// CTA
				{ ref: 'recommendationCard', delay: 800 },
			];

			animationSequence.forEach(({ ref, delay }) => {
				setTimeout(() => {
					const element = this.$refs[ref];
					if (element) {
						// New smooth transition: opacity 0 -> 100, scale 0.95 -> 100, and slight upward move
						element.classList.remove('opacity-0', 'scale-95');
						element.classList.add(
							'opacity-100',
							'scale-100',
							'translate-y-0',
						);

						// Optionally, add a slight upward drift on entry (using translate-y-2 as start)
						element.classList.add('translate-y-2'); // Start slightly below
						setTimeout(() => {
							element.classList.remove('translate-y-2');
						}, 10); // Short delay to ensure transition applies
					}
				}, delay);
			});
		},
	},
};
</script>

<style scoped>
/* Utility classes inherit from Tailwind config */
</style>
